/**
 * 当前模块状态
 *
 * 登录后不自动进入模块，先展示模块首页（三个模块），由用户选择。
 * localStorage 只记录「上次使用的模块」，用于模块首页标记，不用于自动跳转。
 */
import { useCallback, useState } from 'react';
import { AppModuleId, isValidModuleId } from './modules';

function lastUsedKey(userId?: number): string {
  return `winbid_last_module_${userId ?? 'guest'}`;
}

export interface ActiveModuleState {
  /** 当前进入的模块；null 表示停留在模块首页 */
  activeModule: AppModuleId | null;
  /** 上次使用的模块，用于模块首页标记 */
  lastUsedModuleId: AppModuleId | null;
  /** 进入指定模块 */
  selectModule: (id: AppModuleId) => void;
  /** 返回模块首页（不清除上次使用记录） */
  backToModuleHome: () => void;
}

export function useActiveModule(userId?: number): ActiveModuleState {
  const [activeModule, setActiveModule] = useState<AppModuleId | null>(null);
  const [lastUsedModuleId, setLastUsedModuleId] = useState<AppModuleId | null>(() => {
    try {
      const saved = localStorage.getItem(lastUsedKey(userId));
      return isValidModuleId(saved) ? saved : null;
    } catch {
      return null;
    }
  });

  const selectModule = useCallback(
    (id: AppModuleId) => {
      setActiveModule(id);
      setLastUsedModuleId(id);
      try {
        localStorage.setItem(lastUsedKey(userId), id);
      } catch {
        // localStorage 不可用时不影响模块切换
      }
    },
    [userId]
  );

  const backToModuleHome = useCallback(() => {
    setActiveModule(null);
  }, []);

  return { activeModule, lastUsedModuleId, selectModule, backToModuleHome };
}
